"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { cn } from "@/lib/utils";

type Props = {
  squareSize?: number;
  gridGap?: number;
  flickerChance?: number;
  color?: string;
  width?: number;
  height?: number;
  className?: string;
  maxOpacity?: number;
};

export function FlickeringGrid({
  squareSize = 4,
  gridGap = 6,
  flickerChance = 0.3,
  color = "rgb(0, 0, 0)",
  width,
  height,
  className,
  maxOpacity = 0.3,
}: Props) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const isInViewRef = useRef(false);
  // `tick` is included in the RAF effect's deps so the animation loop
  // (re)starts when the IntersectionObserver toggles visibility. Without
  // this, forceTick re-renders the component but the effect would never
  // re-run, leaving the canvas allocated but never drawn (blank).
  const [tick, forceTick] = useState(0);

  const rgbaPrefix = useMemo(() => {
    if (typeof window === "undefined") return "rgba(0,0,0,";
    const c = document.createElement("canvas");
    c.width = 1;
    c.height = 1;
    const ctx = c.getContext("2d");
    if (!ctx) return "rgba(255,0,0,";
    ctx.fillStyle = color;
    ctx.fillRect(0, 0, 1, 1);
    const [r, g, b] = Array.from(ctx.getImageData(0, 0, 1, 1).data);
    return `rgba(${r}, ${g}, ${b},`;
  }, [color]);

  const gridParamsRef = useRef<{
    cols: number;
    rows: number;
    squares: Float32Array;
    dpr: number;
  } | null>(null);

  const setupCanvas = useCallback(
    (canvas: HTMLCanvasElement, w: number, h: number) => {
      const dpr = window.devicePixelRatio || 1;
      canvas.width = w * dpr;
      canvas.height = h * dpr;
      canvas.style.width = `${w}px`;
      canvas.style.height = `${h}px`;
      const cols = Math.floor(w / (squareSize + gridGap));
      const rows = Math.floor(h / (squareSize + gridGap));
      const squares = new Float32Array(cols * rows);
      for (let i = 0; i < squares.length; i++) {
        squares[i] = Math.random() * maxOpacity;
      }
      gridParamsRef.current = { cols, rows, squares, dpr };
    },
    [squareSize, gridGap, maxOpacity]
  );

  // One-time setup: size the canvas, attach observers. Re-runs only when
  // dimensional inputs change (width/height/squareSize/gridGap/color),
  // NOT on isInView toggles — that prevented the scroll-down jank where
  // every viewport entry would tear down and re-create observers.
  useEffect(() => {
    const canvas = canvasRef.current;
    const container = containerRef.current;
    if (!canvas || !container) return;

    const updateCanvasSize = () => {
      const w = width ?? container.clientWidth;
      const h = height ?? container.clientHeight;
      if (w === 0 || h === 0) return;
      setupCanvas(canvas, w, h);
    };

    updateCanvasSize();

    const ro = new ResizeObserver(updateCanvasSize);
    ro.observe(container);

    const io = new IntersectionObserver(
      ([entry]) => {
        const next = entry.isIntersecting;
        if (next === isInViewRef.current) return;
        isInViewRef.current = next;
        forceTick((n) => n + 1);
      },
      { threshold: 0 }
    );
    io.observe(canvas);

    return () => {
      ro.disconnect();
      io.disconnect();
    };
  }, [setupCanvas, width, height]);

  // RAF loop — only runs while in view. The `forceTick` from the IO callback
  // re-runs THIS effect (cheap) which starts/stops the loop. Canvas setup
  // and observers are NOT recreated.
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    let rafId = 0;
    let lastTime = 0;
    let stopped = false;

    const animate = (time: number) => {
      if (stopped) return;
      const params = gridParamsRef.current;
      if (!params) {
        rafId = requestAnimationFrame(animate);
        return;
      }
      const dt = (time - lastTime) / 1000;
      lastTime = time;

      // Update
      for (let i = 0; i < params.squares.length; i++) {
        if (Math.random() < flickerChance * dt) {
          params.squares[i] = Math.random() * maxOpacity;
        }
      }

      // Draw
      ctx.clearRect(0, 0, ctx.canvas.width, ctx.canvas.height);
      for (let i = 0; i < params.cols; i++) {
        for (let j = 0; j < params.rows; j++) {
          const opacity = params.squares[i * params.rows + j];
          ctx.fillStyle = `${rgbaPrefix}${opacity})`;
          ctx.fillRect(
            i * (squareSize + gridGap) * params.dpr,
            j * (squareSize + gridGap) * params.dpr,
            squareSize * params.dpr,
            squareSize * params.dpr
          );
        }
      }
      rafId = requestAnimationFrame(animate);
    };

    if (isInViewRef.current) {
      rafId = requestAnimationFrame(animate);
    }

    return () => {
      stopped = true;
      cancelAnimationFrame(rafId);
    };
  }, [flickerChance, maxOpacity, rgbaPrefix, squareSize, gridGap, tick]);

  return (
    <div
      ref={containerRef}
      aria-hidden="true"
      className={cn("pointer-events-none h-full w-full", className)}
    >
      <canvas ref={canvasRef} className="block h-full w-full" />
    </div>
  );
}
